import React, { useCallback, useMemo, useRef } from 'react';
import { Option } from '@lib/interfaces/form';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import AssetAdd from 'components/Asset/AssetAdd/AssetAdd';
import AssetEdit from 'components/Asset/AssetEdit/AssetEdit';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import { SubmitAction } from '@lib/enums/form';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationLinkLinkableTypeEnum,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useLibModalManagement from '@lib/hooks/useModalManagement';
import AssetFieldNames from '@lib/enums/fieldNames/assetFieldNames';
import useSearchListQueries from './utils/useSearchListQueries';
import useModalManagement from './utils/useModalManagement';
import useAssetActions from './utils/useAssetActions';
import useActions from './utils/useActions';
import useDocumentationLinksPrefill from './utils/useDocumentationLinksPrefill';
import { AssetViewMode } from './enums';

type Props = {
  viewMode: AssetViewMode;
};

export default function AssetFormContainer({ viewMode }: Props) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const submitNewItemRef = useRef<SubmitAction>(undefined);
  const useModalManagementResult = useModalManagement();

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  // BE AssetCategoryPolicy#create? requires :write — hide the create-new
  // affordance for any non-Write level (Read or LimitedRead) so users can't
  // trigger a mutation the server will reject.
  const isCategoriesViewOnly =
    getUserAccessByComponent(AccessComponentEnum.AssetCategoriesManagement) !==
    AccessLevelEnum.Write;
  const isDocumentationPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // Asset documents are allowed on free plan (per EDM accessibility scopes)
  // — only a user-role restriction should hide the linked-documents
  // section. Plan-lock is tracked separately to drop the QR-visibility map
  // (a premium feature) at the modal level.
  const isDocumentationLocked =
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  const initialDocumentationLinksRef = useRef<Option[]>([]);

  const useActionsResult = useActions({
    id,
    viewMode,
  });
  const useAssetActionsResult = useAssetActions({
    formRef,
    initialDocumentationLinksRef,
    // On Free plan, BE assetCreate / assetUpdate accept `documentationLinks`
    // only with exactly one entry and no `unlink: true`. The mutation prep
    // switches to sync-to-one semantics when this flag is set.
    isPlanLocked: isDocumentationPlanLocked,
    useModalManagementResult,
    viewMode,
    useActionsResult,
    submitNewItemRef,
  });
  const useSearchListQueriesResult = useSearchListQueries();

  // Documents modal
  const SELECT_DOCUMENTS_MODAL = 'select-documents';
  const useDocumentsModalResult = useLibModalManagement([
    SELECT_DOCUMENTS_MODAL,
  ]);

  const openDocumentsModal = useCallback(() => {
    useDocumentsModalResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useDocumentsModalResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useDocumentsModalResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useDocumentsModalResult]);

  const onDocumentsSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          AssetFieldNames.DocumentationLinks,
          values.length
            ? values.map((docId) => {
                const visibility = visibilityMap?.[docId];
                return visibility === undefined
                  ? { value: docId }
                  : { value: docId, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const isDocumentsModalOpen = useDocumentsModalResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  const documentationsInitialVisibilityMap = useMemo(() => {
    if (!isDocumentsModalOpen) return undefined;
    const list = formRef.current?.values?.[
      AssetFieldNames.DocumentationLinks
    ] as Array<{ value?: string; visibleViaQr?: boolean }> | undefined;
    if (!Array.isArray(list)) return {};
    return list.reduce<Record<string, boolean>>((acc, item) => {
      const docId = String(item?.value ?? '');
      if (!docId) return acc;
      if (item?.visibleViaQr === undefined) return acc;
      acc[docId] = item.visibleViaQr;
      return acc;
    }, {});
  }, [isDocumentsModalOpen]);

  const { loading: documentationLinksPrefillLoading } =
    useDocumentationLinksPrefill({
      formRef,
      assetId: viewMode === AssetViewMode.Edit ? id : undefined,
      initialDocumentationLinksRef,
      skip: isDocumentationLocked,
    });

  const { assetQueryHook } = useActionsResult;

  if (
    useActionsResult.assetQueryHook.firstLoading ||
    documentationLinksPrefillLoading
  ) {
    return <CircleLoader />;
  }

  if (assetQueryHook.error) {
    return (
      <ErrorPage
        status={getStatusFromError(assetQueryHook.error)}
        description={getDescriptionFromError(assetQueryHook.error)}
      />
    );
  }

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: useAssetActionsResult.defaultValues,
        })}
      />
      {viewMode === AssetViewMode.Add && (
        <AssetAdd
          formRef={formRef}
          openDocumentsModal={openDocumentsModal}
          submitNewAssetRef={submitNewItemRef}
          useAssetActionsResult={useAssetActionsResult}
          useModalManagementResult={useModalManagementResult}
          useSearchListQueriesResult={useSearchListQueriesResult}
          isCategoriesViewOnly={isCategoriesViewOnly}
          isDocumentationLocked={isDocumentationLocked}
          isDocumentationPlanLocked={isDocumentationPlanLocked}
        />
      )}
      {viewMode === AssetViewMode.Edit && (
        <AssetEdit
          formRef={formRef}
          openDocumentsModal={openDocumentsModal}
          submitNewAssetRef={submitNewItemRef}
          useActionsResult={useActionsResult}
          useAssetActionsResult={useAssetActionsResult}
          useModalManagementResult={useModalManagementResult}
          useSearchListQueriesResult={useSearchListQueriesResult}
          isCategoriesViewOnly={isCategoriesViewOnly}
          isDocumentationLocked={isDocumentationLocked}
          isDocumentationPlanLocked={isDocumentationPlanLocked}
        />
      )}
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={AssetFieldNames.DocumentationLinks}
        linkableId={viewMode === AssetViewMode.Edit ? id : undefined}
        linkableType={DocumentationLinkLinkableTypeEnum.Asset}
        // Free-plan assetCreate / assetUpdate accept exactly one
        // `documentationLinks` entry (the BE rejects anything else with
        // `exactly_one_on_free_plan`). Cap the picker so the form can't
        // even produce an invalid payload.
        selectionLimit={isDocumentationPlanLocked ? 1 : undefined}
        visibleViaQr={
          isDocumentationPlanLocked
            ? undefined
            : { initialVisibilityMap: documentationsInitialVisibilityMap }
        }
      />
    </>
  );
}
